import { Ban, Copy, ExternalLink, FileDown, Printer } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  LoadingState,
  useToast,
} from '@/components/ui';
import {
  type CertificatePrintOutput,
  useCertificate,
  useRecordCertificatePrint,
  useRevokeCertificate,
} from '@/features/certificates/api';
import { isApiError } from '@/lib/api';
import { whenLabel } from '@/pages/calendar/time';
import { verifyUrl } from '@/pages/clinical/consult-utils';
import { McDocument, McPrintSheet, McQr } from './McCertificate';
import styles from './mc.module.css';
import parity from './parity.module.css';

const MIN_REASON = 5;

/**
 * One MC (prototype DR.mc): the certificate as issued with its QR, the public
 * check link, print, history and, for the issuing doctor, the clinic manager
 * and the super admin, revoke with a reason. Print and PDF first record the
 * request (POST /medical-certificates/:id/print, once per click) and only then
 * open the sheet; a failed record never opens it.
 */
export function CertificateDrawer({
  id,
  overlapsWith,
  onClose,
}: {
  id: string;
  overlapsWith: string[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useCertificate(id);
  const revoke = useRevokeCertificate();
  const [reason, setReason] = useState('');
  const recordPrint = useRecordCertificatePrint();
  const [printing, setPrinting] = useState<CertificatePrintOutput | null>(null);
  // Guards a second click before the pending state re-renders.
  const requesting = useRef(false);
  const donePrinting = useCallback(() => setPrinting(null), []);
  const d = q.data;
  const url = d ? verifyUrl(d.verificationCode) : '';
  const legacy = d?.branch.source === 'current_setup_legacy';
  const printBlock = !d
    ? null
    : d.revoked
      ? t('certificates.printRevoked')
      : !d.branch.address
        ? legacy
          ? t('certificates.printNeedsAddress')
          : t('certificates.printNoIssueAddress')
        : null;
  const startPrint = (output: CertificatePrintOutput) => {
    if (requesting.current || printing || printBlock) return;
    requesting.current = true;
    recordPrint
      .mutateAsync({ id, output })
      .then(() => setPrinting(output))
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: t('certificates.printFailed'),
          body: isApiError(err)
            ? `${err.message} ${t('certificates.printFailedBody')}`
            : t('certificates.printFailedBody'),
          error: err,
        }),
      )
      .finally(() => {
        requesting.current = false;
      });
  };
  const pendingOutput = recordPrint.isPending
    ? recordPrint.variables?.output
    : undefined;
  const submitRevoke = () =>
    revoke
      .mutateAsync({ id, reason: reason.trim() })
      .then(() => {
        setReason('');
        toast.show({ tone: 'success', title: t('certificates.revokedToast') });
      })
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: t('certificates.revokeFailed'),
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );
  return (
    <Dialog
      open
      wide
      title={d?.number ?? t('certificates.title')}
      onClose={onClose}
      footer={
        <>
          {d?.canRevoke && (
            <Button
              variant="danger"
              icon={<Ban size={16} />}
              disabled={reason.trim().length < MIN_REASON}
              loading={revoke.isPending}
              onClick={() => void submitRevoke()}
            >
              {t('certificates.revoke')}
            </Button>
          )}
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>{t('common.close')}</Button>
          {d && (
            <>
              <Button
                icon={<FileDown size={16} />}
                title={t('certificates.pdfHint')}
                disabled={
                  !!printBlock || (!!pendingOutput && pendingOutput !== 'pdf')
                }
                loading={pendingOutput === 'pdf'}
                onClick={() => startPrint('pdf')}
              >
                {t('certificates.pdf')}
              </Button>
              <Button
                variant="primary"
                icon={<Printer size={16} />}
                disabled={
                  !!printBlock || (!!pendingOutput && pendingOutput !== 'print')
                }
                loading={pendingOutput === 'print'}
                onClick={() => startPrint('print')}
              >
                {t('certificates.print')}
              </Button>
            </>
          )}
        </>
      }
    >
      {q.isLoading ? (
        <LoadingState />
      ) : q.error || !d ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className={parity.form}>
          {d.revoked ? (
            <p className={parity.notice} role="status">
              <Badge tone="danger">{t('certificates.revoked')}</Badge>{' '}
              {t('certificates.revokedNote')}
            </p>
          ) : (
            <Badge tone="success">{t('certificates.valid')}</Badge>
          )}
          <div className={styles.wrap}>
            <McDocument d={d} />
            <McQr code={d.verificationCode} />
          </div>
          {legacy && (
            <p className={parity.notice} role="note">
              {t('certificates.legacyAddress')}
            </p>
          )}
          {printBlock && <p className={parity.notice}>{printBlock}</p>}
          <Field label={t('certificates.verifyLink')}>
            <div className={styles.linkRow}>
              <input
                readOnly
                value={url}
                aria-label={t('certificates.verifyLink')}
              />
              <Button
                size="sm"
                icon={<Copy size={14} />}
                onClick={() =>
                  void navigator.clipboard?.writeText(url).then(() =>
                    toast.show({
                      tone: 'success',
                      title: t('certificates.copied'),
                    }),
                  )
                }
              >
                {t('certificates.copy')}
              </Button>
            </div>
          </Field>
          <a className={styles.vlink} href={url} target="_blank" rel="noopener">
            {t('certificates.openCheck')} <ExternalLink size={13} />
          </a>
          <div>
            <span className="eyebrow">{t('certificates.history')}</span>
            <ul className={styles.history}>
              <li>
                <span>{t('certificates.issuedBy')}</span>
                {d.issuedBy.fullName} · {whenLabel(d.issuedAt)}
              </li>
              <li>
                <span>{t('certificates.scans')}</span>
                {d.scans.count}
                {d.scans.lastAt &&
                  ` · ${t('certificates.lastScan', { when: whenLabel(d.scans.lastAt) })}`}
              </li>
              {d.revoked && (
                <li>
                  <span>{t('certificates.revokedBy')}</span>
                  {d.revoked.by?.fullName ?? '—'} · {whenLabel(d.revoked.at)}
                  {d.revoked.reason && ` · ${d.revoked.reason}`}
                </li>
              )}
              {!!overlapsWith.length && (
                <li>
                  <span>{t('certificates.overlaps')}</span>
                  {overlapsWith.join(', ')}
                </li>
              )}
            </ul>
          </div>
          {d.canRevoke && (
            <Field
              label={t('certificates.revokeReason')}
              hint={t('certificates.revokeHint')}
            >
              <textarea
                className={styles.textarea}
                value={reason}
                maxLength={300}
                placeholder={t('certificates.revokePlaceholder')}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          )}
          {printing && <McPrintSheet d={d} onDone={donePrinting} />}
        </div>
      )}
    </Dialog>
  );
}
